import React from 'react';
import { CmhcIcon, CmhcIconSize } from '@cmhc-schl/hearth-ui';
import { LanguageShort } from '@hobt/constants';
import { Box, Tooltip, Typography } from '@mui/material';
import formatAmountWithDollar from 'Feature/V2/HbtPortfolioManagement/utils/FormatAmountWithDollar';
import { getOccupancyTypeLabel } from 'Feature/V2/HbtPortfolioManagement/utils/getOccupancyTypeLable';
import { getRequestTypeLabel } from 'Feature/V2/HbtPortfolioManagement/utils/RequestTypeLabel';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { ApplicationPortfolioDetailPayload } from '../../api/types';
import ErrorMessage, { SpinnerElement } from '../ErrorMessage';
import formatDigits from '../PortfolioCard/utils/FormatDigits';
import styles from './styles.module.scss';
import { getRequestTypeLabel as getRequestTypeLabelForMasterpool } from 'Feature/V2/HbtMasterPool/components/HbtMasterPoolTable/columns/masterPoolInventoryTableCellRenderers';

interface ApplicationOverviewDetailsOptions {
  insuredApprovedLenderCode?: boolean;
  insuredApprovedLenderName?: boolean;
  requestype?: boolean;
  requestReceiptDate?: boolean;
  offerRequestDate?: boolean;
  occupancyType?: boolean;
  termInsurance?: boolean;
  totalLoansSubmitted?: boolean;
  totalAmountSubmitted?: boolean;
}

interface ApplicationOverviewDetailsProps {
  showSpinner?: boolean;
  showErrorMessage?: boolean;
  className?: string;
  data?: ApplicationPortfolioDetailPayload;
  isCancellationLoan: boolean;
  pptCode?: string;
  title?: string;
  termInsuranceToolTipBody?: string;
  totalLoansSubmittedToolTipBody?: string;
  options?: ApplicationOverviewDetailsOptions;
  isItMasterPool?: boolean;
}

export const ApplicationOverviewDetails = ({
  showSpinner,
  showErrorMessage,
  className,
  data,
  isCancellationLoan,
  pptCode,
  options = {},
  title,
  termInsuranceToolTipBody,
  totalLoansSubmittedToolTipBody,
  isItMasterPool = false
}: ApplicationOverviewDetailsProps) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const { t: tMasterpool } = useTranslation('HbtMasterPool');
  const {
    insuredApprovedLenderCode = true,
    insuredApprovedLenderName = true,
    requestype = true,
    requestReceiptDate = true,
    offerRequestDate = true,
    occupancyType = true,
    termInsurance = true,
    totalLoansSubmitted = true,
    totalAmountSubmitted = true
  } = options;

  return (
    <Box
      tabIndex={0}
      role="region"
      aria-label={t('Application Overview Details')}
      aria-busy={Boolean(showSpinner)}
      className={[styles.requestDetails, className].filter(Boolean).join(' ')}
    >
      {showSpinner && <SpinnerElement />}
      {showErrorMessage && (
        <ErrorMessage
          errorType={data?.insuredApprovedLenderCode === null ? 'no_data_error' : 'api_error'}
        />
      )}

      {data && data.insuredApprovedLenderCode !== null && (
        <div
          className={styles.theContent}
          role="region"
          aria-label={t('Application Overview Details Content')}
        >
          <Typography variant="h4" className={styles.headerText} tabIndex={0}>
            {title}
          </Typography>

          <Box
            aria-label={t('PI-Portfolio-Management-Request-Overview-Insured-Approved-Lender-Code')}
          >
            {insuredApprovedLenderCode && (
              <Typography variant="body2" className={styles.requestGroup}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <span id="insuredLenderCodeLabel">
                    {t('PI-Portfolio-Management-Request-Overview-Insured-Approved-Lender-Code')}
                  </span>
                  <strong aria-labelledby="insuredLenderCodeLabel">
                    {data.insuredApprovedLenderCode}
                  </strong>
                </Box>
              </Typography>
            )}

            {insuredApprovedLenderName && (
              <Typography variant="body2" className={styles.requestGroup}>
                <Box sx={{ display: 'flex', gap: 1, marginTop: '20px', alignItems: 'flex-start' }}>
                  <div style={{ flex: '1' }}>
                    <span id="insuredLenderNameLabel">
                      {t('PI-Portfolio-Management-Request-Overview-Insured-Approved-Lender')}
                    </span>
                  </div>
                  <div style={{ flex: '0 0 auto' }}>
                    <strong aria-labelledby="insuredLenderNameLabel">
                      {i18n.language === LanguageShort.English
                        ? data.insuredApprovedLenderName?.englishName
                        : data.insuredApprovedLenderName?.frenchName}
                    </strong>
                  </div>
                </Box>
              </Typography>
            )}

            {(insuredApprovedLenderCode || insuredApprovedLenderName) && (
              <div className={styles.divider} role="separator" aria-hidden="true"></div>
            )}
          </Box>

          <Box aria-label={t('Request Information')} className={styles.requestGroup}>
            {requestype && (
              <Typography variant="body2">
                <span id="requestTypeLabel">{t('Request-Type')}</span>
                <strong aria-labelledby="requestTypeLabel">
                  {isItMasterPool
                    ? getRequestTypeLabelForMasterpool(pptCode || '', tMasterpool)
                    : getRequestTypeLabel(pptCode || '', t)}
                </strong>
              </Typography>
            )}
            {requestReceiptDate && (
              <Typography variant="body2">
                <span id="requestReceiptDateLabel">
                  {t('PI-Portfolio-Management-Request-Overview-Request-Receipt-Date')}
                </span>
                {data.applicationRequestReceiptDate ? (
                  <strong aria-labelledby="requestReceiptDateLabel">
                    {data.applicationRequestReceiptDate}
                  </strong>
                ) : (
                  <CmhcIcon
                    aria-label={t('PI-Portfolio-Management-Request-Overview-Request-Receipt-Date')}
                    size={CmhcIconSize.SMALL}
                    type="icon_indeterminate"
                  />
                )}
              </Typography>
            )}
            {!isCancellationLoan && offerRequestDate && (
              <Typography variant="body2">
                <span id="offerRequestDateLabel">
                  {t('PI-Portfolio-Management-Request-Overview-Offer-Request-Date')}
                </span>
                {data.applicationOfferRequestDate ? (
                  <strong aria-labelledby="offerRequestDateLabel">
                    {data.applicationOfferRequestDate}
                  </strong>
                ) : (
                  <CmhcIcon
                    aria-label={t('PI-Portfolio-Management-Request-Overview-Request-Receipt-Date')}
                    size={CmhcIconSize.SMALL}
                    type="icon_indeterminate"
                  />
                )}
              </Typography>
            )}
            {!isCancellationLoan && occupancyType && (
              <Typography variant="body2">
                <span id="occupancyTypeLabel">
                  {t('PI-Portfolio-Management-Request-Overview-Occupancy-Type')}
                </span>
                <strong aria-labelledby="occupancyTypeLabel">
                  {getOccupancyTypeLabel(data.portfolioOccupancyType as string, t)}
                </strong>
              </Typography>
            )}
          </Box>

          {!isCancellationLoan && termInsurance && (
            <Box aria-label={t('Term Insurance')} className={styles.requestGroup}>
              <Typography variant="body2">
                <div className={styles.containerWithTooltip}>
                  <span id="termInsuranceLabel">{t('Term-Insurance')}</span>
                  <span>
                    <Tooltip
                      title={termInsuranceToolTipBody}
                      arrow
                      placement="top-start"
                      slotProps={{
                        tooltip: { className: styles.requestOverviewDetailsToolTip },
                        arrow: { className: styles.tooltipArrow }
                      }}
                    >
                      <div>
                        <CmhcIcon
                          aria-hidden="true"
                          size={CmhcIconSize.SMALL}
                          type="icon_info_outlined"
                        />
                      </div>
                    </Tooltip>
                  </span>
                </div>
                <strong aria-labelledby="termInsuranceLabel">
                  {data.portfolioTermInsurance === 0 ||
                  data.portfolioTermInsurance === null ||
                  data.portfolioTermInsurance?.toString() === '0' ? (
                    <CmhcIcon
                      aria-label={t(
                        'PI-Portfolio-Management-Request-Overview-Request-Receipt-Date'
                      )}
                      size={CmhcIconSize.SMALL}
                      type="icon_indeterminate"
                    />
                  ) : data.portfolioTermInsurance === 1 ||
                    data.portfolioTermInsurance?.toString() === '1' ? (
                    `${data.portfolioTermInsurance} ${t('PI-Portfolio-Management-Request-Overview-Year')}`
                  ) : (
                    `${data.portfolioTermInsurance} ${t('PI-Portfolio-Management-Request-Overview-Years')}`
                  )}
                </strong>
              </Typography>
            </Box>
          )}

          <div className={styles.divider} aria-hidden="true"></div>

          <Box tabIndex={0} aria-label={t('Portfolio Summary')} className={styles.requestGroup}>
            {totalLoansSubmitted && (
              <Typography variant="body2">
                <div className={styles.containerWithTooltip}>
                  <span id="totalLoansLabel">
                    {t('PI-Portfolio-Management-Request-Overview-Total-Loans-Submitted')}
                  </span>
                  <span>
                    <Tooltip
                      title={totalLoansSubmittedToolTipBody}
                      aria-describedby="totalLoansLabel"
                      placement="top-start"
                      arrow
                      slotProps={{
                        tooltip: { className: styles.requestOverviewDetailsToolTip },
                        arrow: { className: styles.tooltipArrow }
                      }}
                    >
                      <div>
                        <CmhcIcon
                          aria-hidden="true"
                          size={CmhcIconSize.SMALL}
                          type="icon_info_outlined"
                        />
                      </div>
                    </Tooltip>
                  </span>
                </div>
                <strong aria-labelledby="totalLoansLabel">
                  {formatDigits(data.portfolioNumberOfLoansSubmitted ?? 0, i18n.language)}
                </strong>
              </Typography>
            )}
            {!isCancellationLoan && totalAmountSubmitted && (
              <Typography variant="body2">
                <span id="totalAmountLabel">
                  {t('PI-Portfolio-Management-Request-Overview-Total-Amount-Submitted')}
                </span>
                <strong aria-labelledby="totalAmountLabel">
                  {formatAmountWithDollar(
                    data.portfolioAmountSubmitted?.toString() || '',
                    i18n.language
                  )}
                </strong>
              </Typography>
            )}
          </Box>
        </div>
      )}
    </Box>
  );
};
